import { Controller } from "@hotwired/stimulus"
import { useTransition } from 'stimulus-use'

export default class extends Controller {
  static targets = ["menu", "list", "status", "statusButton", "sentIcon", "progressBar"]

  eventCreated = false

  static values = {
    currentColor: String,
    currentColorBar: String,
    currentIconColor: String,
    currentStatus: String,
    percentPaid: { type: Number, default: 0 },
    mode: { type: String, default: "single" },
    colorVariant: { type: String, default: "desktop" }
  }

  COLOR_VARIANT_MAP = {
    mobile_list: [0, 1],
    mobile: [2, 3],
    desktop: [0, 1, 2]
  }

  COLOR_BAR_VARIANT_MAP = {
    mobile_list: [2],
    mobile: [2],
    desktop: [0]
  }

  connect() {
    if (this.hasMenuTarget) useTransition(this, { element: this.menuTarget })

    this.element.addEventListener("update-status", (event) => {
      const params = event.detail
      this.updateStatus(params)
    })

    // TODO przeniesc do osobnego controllera
    this.handlePartialPayment = (event) => {
      const [data] = event.detail
      if (!event.target.action?.includes("partial_payment") || data?.percent_paid === undefined) return
      // aktualizuj tylko dropdown powiązany z tą fakturą (na show page brak invoiceId — akceptuj zawsze)
      if (data.invoice_id && this.element.dataset.invoiceId && this.element.dataset.invoiceId !== String(data.invoice_id)) return
      // pomiń approval dropdown w dual mode — payment sibling obsłuży obie strony przez updateSiblingFromResponse
      if (!this.hasCurrentIconColorValue && this.element.parentElement?.querySelector("[data-dropdown-status-current-icon-color-value]")) return
      this.updateFromResponse(data)
    }
    document.addEventListener("ajax:success", this.handlePartialPayment)

    // obsługa błędów AJAX z modalu partial payment
    this.handlePartialPaymentError = (event) => {
      if (!event.target.action?.includes("partial_payment")) return
      const [xhr] = event.detail
      let msg = "Error"
      try { msg = JSON.parse(xhr.responseText)?.message || msg } catch (e) { /* ignore parse error */ }
      try { $.jGrowl(msg) } catch (e) { alert(msg) }
    }
    document.addEventListener("ajax:error", this.handlePartialPaymentError)
  }

  async toggle() {
    // data-transition-pending sygnalizuje trwanie async enter()/leave() z useTransition.
    // Queryowalne z testów przez wait_for_transitions / assert_no_css("[data-transition-pending]").
    this.element.dataset.transitionPending = ""

    // https://discuss.hotwired.dev/t/add-and-remove-eventlisteners/710
    if (this.eventCreated === false) {
      const fn = this.outsideHide.bind(this)
      this.eventCreated = fn
      document.addEventListener("click", fn)
    } else {
      this.removeEvent()
    }

    try {
      if (this.transitioned) {
        await this.leave()
      } else {
        await this.enter()
      }
    } finally {
      delete this.element.dataset.transitionPending
    }
  }

  hide() {
    this.leave()
    this.removeEvent()
  }

  outsideHide(event) {
    // this.hide(event)
    if (!this.element.contains(event.target) && (this.hasMenuTarget && !this.menuTarget.classList.contains("hidden"))) {
      this.hide()
    }
  }

  removeEvent() {
    if (this.eventCreated !== false) {
      document.removeEventListener("click", this.eventCreated)
      this.eventCreated = false
    }
  }

  disconnect() {
    this.removeEvent()
  }

  changeStatus(event) {
    event.preventDefault()

    if (this.isLoading()) return

    const params = event.params

    this.currentStatusValue = event.target.textContent

    this.startLoading()

    const csrfToken = document.querySelector('meta[name="csrf-token"]')

    fetch(params.url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken ? csrfToken.content : ""
      }
    })
      .then(res => res.json())
      .then((data) => {
        if (!data || data.percent_paid === undefined) return

        // filtr po id faktury: aktualizuj tylko kontrolery tej samej faktury
        // na show page (brak invoiceId) — akceptuj zawsze
        if (data.id && this.element.dataset.invoiceId && this.element.dataset.invoiceId !== String(data.id)) return

        if (this.modeValue === "multiple" && !params.paymentStatus) {
          // broadcast tylko dla approval — płatność jest lokalna (sibling update przez updateSiblingFromResponse)
          this.dispatchUpdateStatus({ ...data, ...params })
        } else {
          this.updateStatus({ ...data, ...params })
        }

        if (params.status === "paid") {
          window.TurboNativeBridge.postMessage("sendEvent", { event: "paid_invoice" })
        }
      }).finally(() => this.stopLoading()).catch((err) => console.log(err))

    this.hide()
  }

  updateStatus(data) {
    if (data.status === "sent") {
      if (this.hasSentIconTarget) {
        this.sentIconTarget.classList.remove("hidden")
      }
    } else {
      this.updateFromResponse(data)
      // optimistic update wyłączony — wszystko aktualizuje AJAX response (updateFromResponse)
      // zostawiamy tylko data-approval-status (potrzebny do logiki w AJAX response handler)
      if (data.status && ["received", "accepted", "rejected"].includes(data.status)) {
        this.element.dataset.approvalStatus = data.status
      }
    }
  }

  // aktualizacja z odpowiedzi backendu — autorytatywne dane o kolorze i progress bar
  updateFromResponse(data) {
    // payment dropdown (hasCurrentIconColorValue) → przekaż dane do siblinga
    // approval dropdown → aktualizuj się bezpośrednio
    if (this.hasCurrentIconColorValue) {
      this.updateSiblingFromResponse(data)
      // aktualizacja własnych ikon payment dropdown (np. po partial_payment z modalu)
      if (data.payment_status) {
        this.updateOwnPaymentIcons(data.payment_status)
      }
      return
    }

    // przejście single→dual: backend ustawił approval_status, ale element nie miał go wcześniej (stara faktura)
    if (data.approval_status && !this.element.dataset.approvalStatus) {
      this.switchToDualMode(data)
      return
    }

    // aktualizacja tekstu statusu — approval_translation dla dual dropdown, translation dla single
    const text = data.approval_translation || data.translation
    if (text && this.hasStatusTarget) {
      this.statusTarget.textContent = text
      this.statusTarget.dispatchEvent(new Event("change"))
    }

    // zachowaj percent_paid i approval_status do przyszłych optimistic updates
    this.percentPaidValue = data.percent_paid
    if (data.approval_status) {
      this.element.dataset.approvalStatus = data.approval_status
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.add("hidden")

      if (data.color_kind === "partial") {
        this.progressBarTarget.classList.remove("hidden")
        this.progressBarTarget.style.width = data.percent_paid + "%"
      }
    }

    this.updateColor(data.colors, this.statusButtonTarget)

    this.updateColorBar(data.colors)

    // aktualizacja payment ikon na sibling dropdown
    if (data.payment_status) {
      this.updateSiblingPaymentIcons(data.payment_status)
    }
  }

  updateSiblingFromResponse(data) {
    const siblingController = this.element.parentElement?.querySelector("[data-dropdown-status-percent-paid-value]")
    if (!siblingController) return

    // percent_paid + approval_status
    siblingController.dataset.dropdownStatusPercentPaidValue = data.percent_paid
    if (data.approval_status) {
      siblingController.dataset.approvalStatus = data.approval_status
    }

    // progress bar — widoczny tylko gdy approval_status === "accepted"
    const bar = siblingController.querySelector("[data-dropdown-status-target='progressBar']")
    const approvalStatus = data.approval_status || siblingController.dataset.approvalStatus
    if (bar) {
      bar.classList.add("hidden")
      if (approvalStatus === "accepted" && data.color_kind === "partial") {
        bar.classList.remove("hidden")
        bar.style.width = data.percent_paid + "%"
      }
    }

    // kolory approval buttona z autorytatywnych danych backendu
    if (data.colors) {
      const btn = siblingController.querySelector("[data-dropdown-status-target='statusButton']")
      if (btn) {
        const oldColor = siblingController.dataset.dropdownStatusCurrentColorValue
        const selectedColors = this.pickColors(data.colors)

        if (!selectedColors) return

        if (oldColor) btn.classList.remove(...oldColor.split(" "))
        btn.classList.add(...selectedColors.split(" "))
        siblingController.dataset.dropdownStatusCurrentColorValue = selectedColors
      }

      this.updateColorBar(data.colors)
    }
  }

  // aktualizacja własnych ikon payment dropdown (wywoływana gdy payment dropdown dostaje response)
  updateOwnPaymentIcons(paymentStatus) {
    const btn = this.statusButtonTarget
    const icons = btn.querySelectorAll("[data-payment-icon]")
    icons.forEach(icon => {
      if (icon.dataset.paymentIcon === paymentStatus) {
        icon.classList.remove("hidden")
      } else {
        icon.classList.add("hidden")
      }
    })
    // aktualizacja tooltip
    if (this.currentStatusValue) {
      btn.title = this.currentStatusValue
    }
  }

  // aktualizacja payment ikon na sibling dropdown
  updateSiblingPaymentIcons(paymentStatus) {
    const siblingPayment = this.element.parentElement?.querySelector(
      "[data-dropdown-status-current-icon-color-value]"
    )
    if (siblingPayment) {
      const btn = siblingPayment.querySelector("[data-dropdown-status-target='statusButton']")
      if (btn) {
        const icons = btn.querySelectorAll("[data-payment-icon]")
        icons.forEach(icon => {
          if (icon.dataset.paymentIcon === paymentStatus) {
            icon.classList.remove("hidden")
          } else {
            icon.classList.add("hidden")
          }
        })
        // aktualizacja tooltip z nowym statusem
        const statusValue = siblingPayment.dataset.dropdownStatusCurrentStatusValue
        if (statusValue) btn.title = statusValue
      }
    }
  }

  // przejście single→dual mode: pokaż ukryty payment dropdown, przefiltruj menu single→approval
  switchToDualMode(data) {
    // ustaw approval_status na elemencie
    this.element.dataset.approvalStatus = data.approval_status

    // pokaż ukryty payment dropdown (sibling z data-hidden-payment-dropdown)
    const hiddenPayment = this.element.parentElement?.querySelector("[data-hidden-payment-dropdown]")
    if (hiddenPayment) {
      hiddenPayment.classList.remove("hidden")
      // aktualizuj ikony payment na podstawie danych backendu
      if (data.payment_status) {
        const icons = hiddenPayment.querySelectorAll("[data-payment-icon]")
        icons.forEach(icon => {
          if (icon.dataset.paymentIcon === data.payment_status) {
            icon.classList.remove("hidden")
          } else {
            icon.classList.add("hidden")
          }
        })
      }
    }

    // przefiltruj menu: zostaw tylko received/accepted/rejected (approval options)
    const approvalStatuses = ["received", "accepted", "rejected"]
    const menuItems = this.menuTarget.querySelectorAll("[data-dropdown-status-status-param]")
    menuItems.forEach(item => {
      if (!approvalStatuses.includes(item.dataset.dropdownStatusStatusParam)) {
        // usuń cały <li> lub sam element jeśli nie jest w <li>
        const li = item.closest("li") || item.closest("button") || item
        li.remove()
      }
    })
    // usuń też button partial payment (nie ma data-dropdown-status-status-param)
    const partialButtons = this.menuTarget.querySelectorAll("[data-target='partialPaymentModal']")
    partialButtons.forEach(btn => btn.remove())

    // aktualizacja kolorów approval buttona
    if (data.colors) {
      this.updateColor(data.colors, this.statusButtonTarget)

      this.updateColorBar(data.colors)
    }

    // aktualizuj tekst statusu na approval label
    const text = data.approval_translation || data.translation
    if (text && this.hasStatusTarget) {
      this.statusTarget.textContent = text
    }

    // progress bar
    this.percentPaidValue = data.percent_paid
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.add("hidden")

      if (data.color_kind === "partial") {
        this.progressBarTarget.classList.remove("hidden")
        this.progressBarTarget.style.width = data.percent_paid + "%"
      }
    }
  }

  dispatchUpdateStatus(params) {
    const event = new CustomEvent("update-status", {
      detail: params
    })

    const allStatusElements = document.querySelectorAll("[data-controller='dropdown-status']")
    allStatusElements.forEach((element) => {
      element.setAttribute("data-dropdown-status-current-status-value", this.currentStatusValue)
      element.dispatchEvent(event)
    })
  }

  setParamsForModal(event) {
    document.getElementById("partial_payment_invoice_id").value = event.params.invoiceId
    document.getElementById("partial_payment_price").value = event.params.price
    this.hide()
  }

  startLoading() {
    this.menuTarget.classList.add("hidden")
    this.element.classList.add("is-loading")
  }

  stopLoading() {
    this.element.classList.remove("is-loading")
  }

  isLoading() {
    return this.element.classList.contains("is-loading")
  }

  updateColor(colors, el) {
    const selectedColors = this.pickColors(colors)

    if (!selectedColors) return

    el.classList.remove(...this.currentColorValue.split(" "))
    el.classList.add(...selectedColors.split(" "))
    this.currentColorValue = selectedColors
  }

  updateColorBar(colors) {
    const indexes = this.COLOR_BAR_VARIANT_MAP[this.colorVariantValue] || []
    const selectedColors = indexes.map(i => colors[i]).filter(Boolean).join(" ")

    if (!selectedColors) return

    const statusBar = this.element.closest("[data-target~='statusBar']")
    if (!statusBar) return

    statusBar.classList.remove(...this.currentColorBarValue.split(" "))
    statusBar.classList.add(...selectedColors.split(" "))
    this.currentColorBarValue = selectedColors
  }

  pickColors(colors) {
    const indexes = this.COLOR_VARIANT_MAP[this.colorVariantValue] || []
    return indexes.map(i => colors[i]).filter(Boolean).join(" ")
  }
}
